import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../../components/Panel'
import { Metric } from '../../components/Metric'
import { HeatGrid } from '../../components/charts/HeatGrid'
import { visitorsApi, type TimelineKind } from '../../api/visitors'
import { fmtDate, fmtDateTime, PUBLICATION_NAMES } from '../../lib/format'
import { duration } from '../../lib/visitorsFormat'
import { baselinePoint, scoreTone, weekGrid } from '../../lib/visitorsEngagement'
import { useVisitorsRange } from '../../lib/useVisitorsRange'
import { QueryProblem, Skeleton, VisitorsFrame, linkCls, th } from './VisitorsFrame'
import { btn, COLLECTING_TEXT } from './engagementBits'
import { ScoreDayNote } from './ScoreDayNote'

const FILTERS: { key: string; label: string; kinds: TimelineKind[] | null }[] = [
  { key: 'all', label: 'All', kinds: null },
  { key: 'reads', label: 'Reads', kinds: ['visit', 'read', 'edition', 'download'] },
  { key: 'email', label: 'Email', kinds: ['email_sent', 'email_open', 'email_click'] },
  { key: 'account', label: 'Account & billing', kinds: ['sign_in', 'order', 'refund', 'renewal_due'] },
]
const KIND_LABEL: Record<TimelineKind, string> = {
  visit: 'Visit', read: 'Read', edition: 'Edition', download: 'Download', email_sent: 'Email sent', email_open: 'Email opened', email_click: 'Email clicked',
  sign_in: 'Sign-in', order: 'Payment', refund: 'Refund', renewal_due: 'Renewal due',
}
const KIND_TONE: Partial<Record<TimelineKind, string>> = {
  visit: 'text-radar-cyan', read: 'text-radar-cyan', edition: 'text-radar-cyan', download: 'text-radar-cyan', order: 'text-radar-green', refund: 'text-radar-red',
  renewal_due: 'text-amber-glow',
}
const TONE = { red: 'red', amber: 'amber', green: 'green' } as const
const EVENT_CAP = 500
const LEVEL_OPACITY = [0.06, 0.3, 0.5, 0.75, 1]

export function VisitorsMember() {
  const { pub = '', subscriberId = '' } = useParams()
  const { search } = useVisitorsRange()
  const okPub = Object.hasOwn(PUBLICATION_NAMES, pub)
  const idNum = /^[0-9]{1,18}$/.test(subscriberId) ? Number(subscriberId) : null
  const [filter, setFilter] = useState('all')
  const q = useQuery({
    queryKey: ['visitors-member', pub, subscriberId],
    queryFn: () => visitorsApi.member(pub, idNum ?? 0),
    enabled: okPub && idNum !== null,
    retry: false,
  })
  const base = `/visitors/${pub}`
  const d = q.data
  const kinds = FILTERS.find((f) => f.key === filter)?.kinds ?? null
  const events = d ? d.events.filter((e) => !kinds || kinds.includes(e.kind)) : []

  const right = (
    <Link to={`/subscribers/${subscriberId}`} className={`${linkCls} font-mono text-[12px]`}>open subscriber record →</Link>
  )

  let body
  if (idNum === null) body = <Panel accent="red"><p className="text-paper">Not a member id</p></Panel>
  else if (q.isError) body = <QueryProblem error={q.error} pub={pub} />
  else if (!d) body = <Skeleton h="h-64" />
  else {
    const sc = d.score
    const base90 = baselinePoint(sc.trend, sc.score_day)
    const scoreDelta = sc.today !== null && base90 ? sc.today - base90.score : undefined
    const lastRead = d.events.find((e) => e.kind === 'read' || e.kind === 'edition' || e.kind === 'download')
    const capped = d.events.length >= EVENT_CAP
    const cells = weekGrid(d.reading_grid).map((c) => ({ text: '', opacity: LEVEL_OPACITY[c.level] }))
    body = (
      <>
        {sc.collecting ? <Panel accent="amber"><p className="text-paper">{COLLECTING_TEXT}</p></Panel> : <ScoreDayNote scoreDay={sc.score_day} />}
        <p className="font-mono text-[12px] text-paper-dim">
          {d.status ?? '—'} · plan {d.plan ?? '—'} · auto-renew {d.auto_renew === null ? '—' : d.auto_renew ? 'on' : 'off'} · renews {d.renews_on ? fmtDate(d.renews_on) : '—'}
        </p>
        <Panel accent="amber">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Metric size="sm" tone={sc.today === null ? 'paper' : TONE[scoreTone(sc.today)]} label="Score" value={sc.collecting ? 'Collecting' : sc.today ?? '—'}
                    delta={scoreDelta} sub={scoreDelta !== undefined && base90 ? `vs ${base90.day}` : undefined} />
            <Metric size="sm" label="Last read" value={lastRead ? fmtDate(lastRead.at) : 'never'} />
            <Metric size="sm" label="Reading days (90d)" value={sc.reading_days} />
            <Metric size="sm" label="Engaged time (90d)" value={sc.engaged_ms === null ? null : duration(sc.engaged_ms)} />
            <Metric size="sm" label="Emails opened (90d)" value={sc.email_opens} />
            <Metric size="sm" label="Visitor ids linked" value={d.visitor_ids.length} />
          </div>
        </Panel>
        <Panel label="Reading, last 52 weeks" accent="cyan">
          <HeatGrid ariaLabel="Weekly reading, last 52 weeks" columns={d.reading_grid.map((r) => r.week_start.slice(5))} rows={[{ label: 'Engaged time', cells }]} />
          <p className="mt-2 font-mono text-[11px] text-paper-faint">Each cell is one week, darker = more engaged time than other weeks.</p>
        </Panel>
        {d.visitor_ids.length > 0 && (
          <p className="font-mono text-[12px] text-paper-dim">
            Visitor ids:{' '}
            {d.visitor_ids.map((v) => <Link key={v} to={`${base}/visitors/${encodeURIComponent(v)}?${search}`} className={`${linkCls} mr-3`}>{v.slice(0, 8)}</Link>)}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => <button key={f.key} type="button" aria-pressed={filter === f.key} className={btn(filter === f.key)} onClick={() => setFilter(f.key)}>{f.label}</button>)}
        </div>
        {capped && <p className="font-mono text-[11px] text-paper-faint">Showing the newest {EVENT_CAP} events</p>}
        <Panel label="Timeline" accent="cyan" bodyClassName="p-0">
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-[12px] text-paper">
              <thead><tr className="border-b border-ink-500">{['Time', 'Kind', 'Title', 'Detail', ''].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={`${e.at}-${e.kind}-${i}`} className="border-b border-ink-500/50">
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(e.at)}</td>
                    <td className={`px-3 py-2 whitespace-nowrap ${KIND_TONE[e.kind] ?? 'text-paper-dim'}`}>{KIND_LABEL[e.kind] ?? e.kind}</td>
                    <td className="px-3 py-2 max-w-[320px] truncate" title={e.title}>{e.title}</td>
                    <td className="px-3 py-2 text-paper-dim max-w-[320px] truncate" title={e.detail}>{e.detail}</td>
                    <td className="px-3 py-2">
                      {e.link && <Link to={`${base}/${e.link.type === 'session' ? 'sessions' : 'events'}/${encodeURIComponent(String(e.link.id))}?${search}`} className={linkCls}>open</Link>}
                    </td>
                  </tr>
                ))}
                {events.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-paper-faint">Nothing in this view</td></tr>}
              </tbody>
            </table>
          </div>
        </Panel>
      </>
    )
  }
  return (
    <VisitorsFrame pub={pub} valid={okPub} title={`Member #${subscriberId}`} crumbs={[{ label: `Member #${subscriberId}` }]} right={right}>
      {body}
    </VisitorsFrame>
  )
}
